import { useState } from 'react';
import { Link } from 'react-router-dom';
import { XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  useSupportCaseDetail,
  useSubmitSupportAction,
  useResolveSupportCase,
  useBookingTimeline,
} from '../hooks/useSupportCases';
import { useBooking } from '../hooks/useBookings';
import { SupportCaseStatusBadge } from './SupportCaseStatusBadge';
import { SupportCaseActionForm } from './SupportCaseActionForm';
import { BookingJourneyTimeline } from './BookingJourneyTimeline';
import type { SupportOutcome } from '../types/supportCase';

const OUTCOME_I18N: Record<
  SupportOutcome,
  | 'badges:outcomeSuccess'
  | 'badges:outcomeNotAllowed'
  | 'badges:outcomeAlreadyCompleted'
  | 'badges:outcomeStaleUpdate'
  | 'badges:outcomeNotFound'
> = {
  SUCCESS: 'badges:outcomeSuccess',
  NOT_ALLOWED: 'badges:outcomeNotAllowed',
  ALREADY_COMPLETED: 'badges:outcomeAlreadyCompleted',
  STALE_UPDATE: 'badges:outcomeStaleUpdate',
  NOT_FOUND: 'badges:outcomeNotFound',
};

interface Props {
  readonly caseId: string;
  readonly onClose: () => void;
}

export const SupportCaseDetailPanel = ({ caseId, onClose }: Props) => {
  const { t } = useTranslation(['badges', 'pages', 'errors']);
  const [showActionForm, setShowActionForm] = useState(false);

  const {
    data: supportCase,
    isLoading,
    isError,
  } = useSupportCaseDetail(caseId);

  const submitAction = useSubmitSupportAction();
  const resolveCase = useResolveSupportCase();

  const bookingId = supportCase?.bookingId;
  const bookingQuery = useBooking(bookingId);
  const booking = bookingQuery.data;
  const timelineQuery = useBookingTimeline(bookingId);

  const isTerminal =
    supportCase?.status === 'RESOLVED' || supportCase?.status === 'ABANDONED';

  function handleSubmitAction(params: {
    actionType: Parameters<typeof submitAction.mutate>[0]['request']['actionType'];
    idempotencyKey: string;
    expectedBookingVersion?: number;
    note?: string;
  }) {
    submitAction.mutate(
      {
        caseId,
        request: {
          actionType: params.actionType,
          idempotencyKey: params.idempotencyKey,
          expectedBookingVersion: params.expectedBookingVersion,
          note: params.note,
        },
      },
      {
        onSuccess: () => setShowActionForm(false),
      },
    );
  }

  return (
    <aside
      data-testid="support-case-detail-panel"
      className="fixed inset-y-0 right-0 w-[480px] bg-white border-l border-gray-200 shadow-xl flex flex-col z-20 overflow-y-auto"
      aria-label={t('pages:supportCaseDetailsAriaLabel')}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
        <h2 className="text-base font-semibold text-gray-900">
          {t('pages:supportCaseDetailsHeading')}
        </h2>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-gray-400 hover:text-gray-600"
        >
          <XCircle className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 p-4 space-y-6">
        {isLoading && (
          <p className="text-sm text-gray-500">{t('pages:loadingCaseDetails')}</p>
        )}

        {isError && (
          <p className="text-sm text-red-600">{t('pages:failedToLoadCaseDetails')}</p>
        )}

        {supportCase && (
          <>
            {/* Case summary */}
            <section className="space-y-2">
              <div className="flex items-center gap-3">
                <SupportCaseStatusBadge status={supportCase.status} />
                <span className="text-xs text-gray-500 font-mono">{supportCase.caseId}</span>
              </div>
              <p className="text-sm text-gray-600">
                {t('pages:supportCaseOpenedBy', {
                  actorType: supportCase.actorType,
                  date: new Date(supportCase.openedAt).toLocaleDateString(),
                })}
              </p>
              {supportCase.note && (
                <p className="text-sm text-gray-700">
                  {t('pages:supportCaseNote', { note: supportCase.note })}
                </p>
              )}
              {booking && (
                <Link
                  to={`/bookings?bookingId=${booking.bookingId}`}
                  className="text-sm text-primary-600 hover:underline"
                >
                  {t('pages:supportCaseBookingLink')}
                </Link>
              )}
            </section>

            {/* Actions history */}
            <section>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {t('pages:sectionCaseHistory')}
              </h3>
              {supportCase.actions.length === 0 ? (
                <p className="text-sm text-gray-400 italic">{t('pages:noActionsYet')}</p>
              ) : (
                <ol className="space-y-3">
                  {supportCase.actions.map(action => (
                    <li key={action.actionId} className="text-sm border border-gray-100 rounded p-3 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">
                          {t(OUTCOME_I18N[action.outcome ?? 'SUCCESS'] ?? 'badges:outcomeSuccess')}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(action.performedAt).toLocaleString()}
                        </span>
                      </div>
                      {action.outcome && (
                        <p className="text-xs text-gray-600">
                          {t('pages:actionOutcome', {
                            outcome: t(OUTCOME_I18N[action.outcome]),
                          })}
                        </p>
                      )}
                      {action.outcome === 'STALE_UPDATE' && (
                        <p className="text-xs text-amber-700 mt-1">
                          {t('pages:staleUpdateGuidance')}
                        </p>
                      )}
                      {action.outcomeNote && (
                        <p className="text-xs text-gray-500">{action.outcomeNote}</p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </section>

            {/* Booking journey timeline */}
            {bookingId && (
              <section>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  {t('pages:timelineHeading')}
                </h3>
                <BookingJourneyTimeline
                  entries={timelineQuery.data?.entries ?? []}
                  isLoading={timelineQuery.isLoading}
                  isError={timelineQuery.isError}
                />
              </section>
            )}

            {/* Resolve */}
            {!isTerminal && (
              <section className="border-t border-gray-100 pt-4">
                {!showActionForm ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowActionForm(true)}
                      className="flex-1 bg-primary-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-primary-700"
                    >
                      {t('pages:sectionSubmitAction')}
                    </button>
                    <button
                      onClick={() => resolveCase.mutate(caseId)}
                      disabled={resolveCase.isPending}
                      className="px-4 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {resolveCase.isPending
                        ? t('pages:resolving')
                        : t('pages:resolveCase')}
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      {t('pages:sectionSubmitAction')}
                    </h3>
                    {submitAction.isError && (
                      <p className="text-sm text-red-600 mb-2">
                        {t('errors:failedToSubmitAction')}
                      </p>
                    )}
                    <SupportCaseActionForm
                      supportCase={supportCase}
                      booking={booking}
                      isSubmitting={submitAction.isPending}
                      onSubmit={handleSubmitAction}
                      onCancel={() => setShowActionForm(false)}
                    />
                  </>
                )}
                {resolveCase.isError && (
                  <p className="text-sm text-red-600 mt-2">
                    {t('errors:failedToResolveCase')}
                  </p>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </aside>
  );
};
